/**
 * Navigation Resolver
 *
 * Issue #356: Persona Guide Integration with Navigation-Aware Retrieval
 *
 * Resolves route keys to URLs at runtime, supporting:
 * - Dynamic URL resolution (URLs can change without breaking links)
 * - Permission filtering
 * - Capability filtering
 * - Portal-aware resolution
 */

import { PORTS } from '../config/ports';
import {
  getBreadcrumbs as getRouteBreadcrumbs,
  getChildRoutes as getRouteChildren,
  getRouteDefinition,
  getPortalRoutes as getRoutesForPortal,
  ROUTE_REGISTRY,
  type RouteDefinition,
  type RouteKey,
  searchRoutes as searchRoutesInternal,
} from './route-keys';
import type { NavPortalType, PermissionRule } from './types';

// ============================================================================
// Types
// ============================================================================

/**
 * User context for permission-aware route resolution
 */
export interface UserRouteContext {
  /** User's role */
  role?: string;

  /** User's permissions (CASL-style) */
  permissions?: PermissionRule[];

  /** Business capabilities (for capability-gated routes) */
  capabilities?: Record<string, boolean>;

  /** Current portal */
  portal?: NavPortalType;
}

/**
 * Resolved route with full URL
 */
export interface ResolvedRoute {
  /** Route key */
  key: RouteKey;

  /** Resolved URL (full path with base) */
  url: string;

  /** Route title */
  title: string;

  /** Route description */
  description?: string;

  /** Whether the user can access this route */
  accessible: boolean;

  /** Reason if not accessible */
  inaccessibleReason?: string;
}

/**
 * Breadcrumb item for navigation
 */
export interface Breadcrumb {
  /** Route key */
  key: RouteKey;

  /** Display title */
  title: string;

  /** URL to navigate to */
  url: string;
}

/**
 * Options for route resolution
 */
export interface ResolveOptions {
  /** ID parameter for detail routes */
  id?: string;

  /** Additional query parameters */
  query?: Record<string, string>;

  /** Whether to include base URL */
  includeBase?: boolean;
}

// ============================================================================
// Portal Base URLs
// ============================================================================

/**
 * Get base URL for a portal
 */
function getPortalBaseUrl(portal: NavPortalType, includeBase = false): string {
  if (!includeBase) return '';

  switch (portal) {
    case 'admin':
      return `http://localhost:${PORTS.ADMIN_PORTAL}`;
    case 'business':
      return `http://localhost:${PORTS.BUSINESS_PORTAL}`;
    case 'consumer':
      return `http://localhost:${PORTS.CONSUMER_PORTAL}`;
    case 'auth':
      return `http://localhost:${PORTS.AUTH_PORTAL}`;
    default:
      return '';
  }
}

// ============================================================================
// Navigation Resolver Class
// ============================================================================

/**
 * Navigation Resolver
 *
 * Provides methods for resolving route keys to URLs and checking access.
 */
export class NavigationResolver {
  private readonly context: UserRouteContext;

  constructor(context: UserRouteContext = {}) {
    this.context = context;
  }

  /**
   * Resolve a route key to a full URL
   *
   * @param key - Route key to resolve
   * @param options - Resolution options (id, query params, etc.)
   * @returns Resolved URL or null if route not found
   */
  resolveRoute(key: RouteKey, options: ResolveOptions = {}): string | null {
    const definition = getRouteDefinition(key);
    if (!definition) return null;

    let path = definition.path;

    // Replace ID placeholder if present
    if (definition.requiresId && options.id) {
      path = path.replace(':id', options.id);
    } else if (definition.requiresId && !options.id) {
      // Can't resolve detail route without ID
      return null;
    }

    // Add query parameters
    if (options.query && Object.keys(options.query).length > 0) {
      const queryString = new URLSearchParams(options.query).toString();
      path = `${path}?${queryString}`;
    }

    // Add base URL if requested
    const base = getPortalBaseUrl(definition.portal, options.includeBase);
    return `${base}${path}`;
  }

  /**
   * Resolve a route with full metadata
   *
   * @param key - Route key to resolve
   * @param options - Resolution options
   * @returns Resolved route with accessibility info
   */
  resolve(key: RouteKey, options: ResolveOptions = {}): ResolvedRoute | null {
    const definition = getRouteDefinition(key);
    if (!definition) return null;

    const url = this.resolveRoute(key, options);
    if (!url) return null;

    const accessCheck = this.checkAccess(definition);

    return {
      key,
      url,
      title: definition.title,
      description: definition.description,
      accessible: accessCheck.accessible,
      inaccessibleReason: accessCheck.reason,
    };
  }

  /**
   * Get all routes for a persona/role
   *
   * @param portal - Portal to get routes for
   * @returns Array of accessible routes for the portal
   */
  getRoutesByPortal(portal: NavPortalType): ResolvedRoute[] {
    const routes = getRoutesForPortal(portal);
    return routes
      .filter((route) => !route.requiresId) // Exclude detail routes from listing
      .map((route) => this.resolve(route.key))
      .filter((r): r is ResolvedRoute => r !== null);
  }

  /**
   * Get accessible routes for the current user
   *
   * @param portal - Optional portal filter
   * @returns Array of accessible routes
   */
  getAccessibleRoutes(portal?: NavPortalType): ResolvedRoute[] {
    const routes = portal ? getRoutesForPortal(portal) : Object.values(ROUTE_REGISTRY);

    return routes
      .filter((route) => {
        const access = this.checkAccess(route);
        return access.accessible && !route.requiresId;
      })
      .map((route) => this.resolve(route.key))
      .filter((r): r is ResolvedRoute => r !== null);
  }

  /**
   * Get breadcrumbs for a route
   *
   * @param key - Route key
   * @param options - Resolution options (for ID substitution)
   * @returns Array of breadcrumb items
   */
  getBreadcrumbs(key: RouteKey, options: ResolveOptions = {}): Breadcrumb[] {
    const trail = getRouteBreadcrumbs(key);
    return trail.map((route, index) => {
      // Only the last item (detail page) gets the ID
      const isLast = index === trail.length - 1;
      const resolveOptions = isLast ? options : {};

      return {
        key: route.key,
        title: route.title,
        url: this.resolveRoute(route.key, resolveOptions) || route.path,
      };
    });
  }

  /**
   * Get child routes for a parent
   *
   * @param parentKey - Parent route key
   * @returns Array of child routes
   */
  getChildRoutes(parentKey: RouteKey): ResolvedRoute[] {
    const children = getRouteChildren(parentKey);
    return children
      .filter((route) => !route.requiresId)
      .map((route) => this.resolve(route.key))
      .filter((r): r is ResolvedRoute => r !== null);
  }

  /**
   * Search routes by query
   *
   * @param query - Search query
   * @param portal - Optional portal filter
   * @returns Matching routes
   */
  searchRoutes(query: string, portal?: NavPortalType): ResolvedRoute[] {
    const results = searchRoutesInternal(query, portal);
    return results
      .filter((route) => !route.requiresId)
      .map((route) => this.resolve(route.key))
      .filter((r): r is ResolvedRoute => r !== null);
  }

  /**
   * Check if user can access a route
   *
   * @param route - Route definition to check
   * @returns Access check result
   */
  private checkAccess(route: RouteDefinition): { accessible: boolean; reason?: string } {
    // Check capability requirement
    if (route.capability && this.context.capabilities) {
      if (!this.context.capabilities[route.capability]) {
        return {
          accessible: false,
          reason: `Requires capability: ${route.capability}`,
        };
      }
    }

    // Check permission requirement (if we have a CASL-style ability)
    if (route.permission && this.context.permissions) {
      // Simple permission check - in real implementation this would use CASL
      const hasPermission = this.context.permissions.some(
        (p) => p.action === route.permission || p.action === 'manage',
      );
      if (!hasPermission) {
        return {
          accessible: false,
          reason: `Requires permission: ${route.permission}`,
        };
      }
    }

    return { accessible: true };
  }
}

// ============================================================================
// Singleton Instance for Simple Use Cases
// ============================================================================

/**
 * Default resolver instance (no user context)
 *
 * Use this for simple URL resolution without permission checking.
 * For permission-aware resolution, create a new NavigationResolver with context.
 */
export const defaultResolver = new NavigationResolver();

// ============================================================================
// Convenience Functions
// ============================================================================

/**
 * Quick resolve a route key to URL
 *
 * @param key - Route key
 * @param id - Optional ID for detail routes
 * @returns Resolved URL or null
 */
export function resolveRouteUrl(key: RouteKey, id?: string): string | null {
  return defaultResolver.resolveRoute(key, { id });
}

/**
 * Quick resolve with full route info
 *
 * @param key - Route key
 * @param id - Optional ID for detail routes
 * @returns Resolved route or null
 */
export function resolveRoute(key: RouteKey, id?: string): ResolvedRoute | null {
  return defaultResolver.resolve(key, { id });
}

/**
 * Get route title by key
 *
 * @param key - Route key
 * @returns Route title or undefined
 */
export function getRouteTitle(key: RouteKey): string | undefined {
  return getRouteDefinition(key)?.title;
}

/**
 * Get route description by key
 *
 * @param key - Route key
 * @returns Route description or undefined
 */
export function getRouteDescription(key: RouteKey): string | undefined {
  return getRouteDefinition(key)?.description;
}

// ============================================================================
// AI Response Formatting Helpers
// ============================================================================

/**
 * Format a route as a markdown link for AI responses
 *
 * @param key - Route key
 * @param id - Optional ID for detail routes
 * @returns Markdown link string
 */
export function formatRouteLink(key: RouteKey, id?: string): string {
  const route = resolveRoute(key, id);
  if (!route) return key;

  return `[${route.title}](${route.url})`;
}

/**
 * Format multiple routes as a markdown list for AI responses
 *
 * @param keys - Array of route keys
 * @returns Markdown list string
 */
export function formatRouteList(keys: RouteKey[]): string {
  return keys
    .map((key) => {
      const route = resolveRoute(key);
      if (!route) return `- ${key}`;
      return `- [${route.title}](${route.url})${route.description ? `: ${route.description}` : ''}`;
    })
    .join('\n');
}

// ============================================================================
// Persona Guide Integration Helpers (Issue #356)
// ============================================================================

/**
 * Retrieval result from the Retrieval Service that contains persona guide metadata
 */
export interface PersonaGuideRetrievalResult {
  id: string;
  content: string;
  title?: string;
  score: number;
  metadata: {
    persona?: string;
    section?: string;
    portal?: string;
    routeKeys?: string | string[];
    keywords?: string | string[];
    docType?: string;
    [key: string]: unknown;
  };
}

/**
 * Enhanced retrieval result with resolved navigation URLs
 */
export interface EnhancedPersonaGuideResult extends PersonaGuideRetrievalResult {
  /** Resolved navigation URL for the most relevant route key */
  navigationUrl?: string;
  /** All resolved route links */
  resolvedRoutes: Array<{ key: RouteKey; url: string; title: string }>;
}

/**
 * Enhance persona guide retrieval results with navigation URLs
 *
 * Takes retrieval results and resolves route keys to actual URLs.
 * This enables clickable links in AI responses.
 *
 * @param results - Raw retrieval results with persona guide metadata
 * @param userContext - Optional user context for permission filtering
 * @returns Enhanced results with resolved navigation URLs
 */
export function enhanceRetrievalWithNavigation(
  results: PersonaGuideRetrievalResult[],
  userContext?: UserRouteContext,
): EnhancedPersonaGuideResult[] {
  const resolver = userContext ? new NavigationResolver(userContext) : defaultResolver;

  return results.map((result) => {
    const routeKeys = parseRouteKeys(result.metadata.routeKeys);
    const resolvedRoutes: EnhancedPersonaGuideResult['resolvedRoutes'] = [];

    for (const keyStr of routeKeys) {
      // Validate the route key exists in registry
      const key = keyStr as RouteKey;
      if (ROUTE_REGISTRY[key]) {
        const resolved = resolver.resolve(key);
        if (resolved?.accessible) {
          resolvedRoutes.push({
            key,
            url: resolved.url,
            title: resolved.title,
          });
        }
      }
    }

    return {
      ...result,
      navigationUrl: resolvedRoutes[0]?.url,
      resolvedRoutes,
    };
  });
}

/**
 * Format persona guide results as markdown with navigation links
 *
 * @param results - Enhanced retrieval results
 * @returns Formatted markdown string
 */
export function formatGuideResultsAsMarkdown(results: EnhancedPersonaGuideResult[]): string {
  if (results.length === 0) return '';

  const sections = results.map((result) => {
    const title = result.title ?? result.metadata.section ?? 'Guide Section';
    const persona = result.metadata.persona ?? 'Unknown';
    const navLinks = result.resolvedRoutes
      .slice(0, 3) // Limit to 3 links
      .map((r) => `[${r.title}](${r.url})`)
      .join(', ');

    let markdown = `### ${title}\n\n`;
    markdown += `**Persona:** ${formatPersonaName(persona)}\n\n`;

    if (navLinks) {
      markdown += `**Related:** ${navLinks}\n\n`;
    }

    markdown += result.content.slice(0, 500) + (result.content.length > 500 ? '...' : '');
    return markdown;
  });

  return sections.join('\n\n---\n\n');
}

/**
 * Parse route keys from retrieval metadata
 * Handles both comma-separated string and array formats
 */
function parseRouteKeys(routeKeys: string | string[] | undefined): string[] {
  if (!routeKeys) return [];
  if (Array.isArray(routeKeys)) return routeKeys;
  return routeKeys.split(',').map((k) => k.trim());
}

/**
 * Format persona type to human-readable name
 */
function formatPersonaName(persona: string): string {
  return persona
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
